import { useLayoutEffect, useState } from 'react'
import {
  Box,
  Stack,
  styled,
  Typography,
  IconButton,
  CircularProgress,
} from '@mui/material'
import {
  WappStage,
  useWappStage,
  WapprunnerCreate,
} from 'weblite-web-wapprunner'
import { WappMode } from '@weblite-wapps/protocol'
import { useHistoryCustomBack } from 'weblite-router'
import { CloseIcon } from '$common/icons'
import { getWappTheme } from '$common/wapp-bar-theme'
import { PageWithAppBar } from '$common/page-with-app-bar'
import { CallBar } from '$comp/_app/components/chat/components/call-bar'
import { useShowCallBar } from '$logic/call'
import { useWapp } from '$logic/wapps/react-hook'
import { WappPanelHeader } from './components/wapp-panel-header'
import { useCurrentWappMeta } from './react-hooks'
import { WappPanel as WappPanelClass } from '$logic/wapp-panels'
import { NativeColors, useSetNativeColors } from '$logic/native-colors'

const LoadingBox = styled(Box)`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;

  width: 100%;
  height: 100%;
`

const LogoBox = styled(Stack)`
  position: absolute;
  left: 0;
  right: 0;
  top: 35%;

  align-items: center;
  justify-content: center;

  // wapp logo
  & > svg {
    width: 80px;
    height: 80px;
  }

  & path {
    fill: #fff;
  }
`

const LoadingProgress = styled(CircularProgress)`
  color: #fff;
  position: absolute;
  left: 0;
  right: 0;
  bottom: 80px;
  margin: auto;
`

export const wappPanelAPI = {
  //  eslint-disable-next-line @typescript-eslint/no-unused-vars
  setNativeColors(_: NativeColors) {},
}

export function WappPanel() {
  const currentWappMeta = useCurrentWappMeta()
  const { wappSessionId, wappId, wisId, input, parentWapp } =
    currentWappMeta || {}

  const [overriddenNativeColors, setOverriddenNativeColors] = useState<
    NativeColors
  >({})

  useLayoutEffect(() => {
    wappPanelAPI.setNativeColors = (newColors: NativeColors) =>
      setOverriddenNativeColors(colors => ({
        statusBar: newColors.statusBar || colors.statusBar,
        bottomSafeArea: newColors.bottomSafeArea || colors.bottomSafeArea,
      }))
  }, [])

  useSetNativeColors(
    () => overriddenNativeColors,
    [overriddenNativeColors],
    'wappSessionId' in (currentWappMeta || {}),
  )

  const { entity: wapp, loading } = useWapp(wappId) || {}
  // TODO: hideHeader should be added to wapp type in ts action
  const { hideHeader } = wapp || {}

  const wappStage = useWappStage(currentWappMeta?.wappSessionId)
  const showCallBar = useShowCallBar()

  useHistoryCustomBack(() => WappPanelClass.popFromPanel(), [])

  if (!currentWappMeta || !('wappSessionId' in currentWappMeta)) return null

  const isLoading = wappStage === WappStage.LOADING
  const { name, icon, loadingGradient } = getWappTheme(wappId)

  return (
    <PageWithAppBar
      appBar={
        <>
          {showCallBar && <CallBar />}
          {!isLoading && !loading && !hideHeader && (
            <WappPanelHeader wappId={wappId} wappSessionId={wappSessionId} />
          )}
        </>
      }
    >
      <Box sx={{ position: 'relative', height: '100%' }}>
        <WapprunnerCreate
          key={wappSessionId}
          wappSessionId={wappSessionId}
          wappId={wappId}
          wisId={wisId}
          input={input}
          parentWapp={parentWapp}
          mode={WappMode.MAIN}
        />

        {isLoading && (
          <LoadingBox sx={{ background: loadingGradient }}>
            <IconButton
              sx={{ position: 'absolute', left: '15px', top: '15px' }}
              onClick={() => WappPanelClass.closeTopWapp()}
            >
              <CloseIcon />
            </IconButton>

            <LogoBox>
              {icon}
              <Typography
                sx={{
                  fontSize: '24px',
                  fontWeight: 'bold',
                  color: '#fff',
                  marginTop: '20px',
                }}
              >
                {name}
              </Typography>
            </LogoBox>

            <LoadingProgress />
          </LoadingBox>
        )}
      </Box>
    </PageWithAppBar>
  )
}
